/**
 * 会话持久化（localStorage 读写，供刷新后恢复登录态）。
 *
 * - 仅存客户端会话快照（staffId/name/token/positions/organizationId），不存权限快照——
 *   权限每次启动经 GET /auth/permissions 重新拉取，保证权限矩阵变更后刷新即生效；
 * - 会话令牌服务端有效期 72h（staff_tokens 表），过期后端返回 401，Transport 触发
 *   清空会话 + 重定向登录页，不会出现假登录态；
 * - 读写均防御性 try/catch（隐私模式/存储禁用/JSON 损坏时静默返回 null，不阻断应用启动）。
 * 对齐 admin-web-shell:SC-09/SC-10（会话设置与清空的持久化延伸）。
 */
import type { SessionState } from './SessionContext';

/** localStorage 键名（含应用前缀，防同域多应用串写）。 */
const STORAGE_KEY = 'admin-web.session.v1';

/** 必需字段齐全才视为合法快照（防半写/损坏数据进入会话）。 */
function isValidSession(v: unknown): v is SessionState {
  if (!v || typeof v !== 'object') return false;
  const s = v as Record<string, unknown>;
  return (
    typeof s.staffId === 'string' &&
    typeof s.name === 'string' &&
    typeof s.token === 'string' &&
    typeof s.organizationId === 'string' &&
    Array.isArray(s.positions) &&
    s.positions.every((p) => typeof p === 'string')
  );
}

/** 读取持久化会话（无/损坏/存储禁用时返回 null）。 */
export function loadPersistedSession(): SessionState | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** 持久化会话（登录/会话变更时调用）。 */
export function persistSession(session: SessionState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // 存储禁用/超配额：仅失去刷新恢复能力，不影响当前会话
  }
}

/** 清除持久化会话（登出/401 时调用）。 */
export function clearPersistedSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 同上，静默降级
  }
}
